import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  AlertOctagon, CalendarClock, ClipboardCopy, ExternalLink, HandCoins, Loader2,
  MessageCircle, MoreHorizontal, ShieldOff,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/recouvrement`;

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Menu « Autres actions » du panneau facture : promesse, litige, perte, planification… */
export const DunningMoreActions = ({ invoiceRef, detail, caseData, onWhatsApp, onChanged }) => {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null); // 'promise' | 'dispute' | 'writeOff' | 'schedule'
  const [busy, setBusy] = useState(false);
  const [promiseDate, setPromiseDate] = useState(inDays(7));
  const [promiseAmount, setPromiseAmount] = useState(String(Math.round(detail?.amountDue || 0)));
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [scheduleDate, setScheduleDate] = useState(inDays(7));
  const [scheduleChannel, setScheduleChannel] = useState('email');

  const close = () => { setDialog(null); setReason(''); setNote(''); };

  const call = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      onChanged && onChanged();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action impossible', { duration: 7000 });
    } finally { setBusy(false); }
  };

  const copyRecap = async () => {
    const text = caseData?.recapText || `Facture ${invoiceRef}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Récapitulatif copié dans le presse-papiers');
    } catch {
      toast.info(text, { duration: 10000 });
    }
  };

  const openLinked = () => {
    if (detail?.invoiceType === 'operation' && detail?.operationId) {
      navigate(`/admin/operations?focus=${detail.operationId}`);
    } else if (caseData?.contractId) {
      navigate(`/admin/contracts/${caseData.contractId}`);
    } else {
      toast.info('Aucune opération ni contrat lié à cette facture');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid="rec-action-more"
            className="flex flex-col items-center justify-center gap-1.5 rounded-lg border border-red-200 p-3 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50">
            <MoreHorizontal className="h-4 w-4" />
            Autres actions
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid="rec-more-menu">
          <DropdownMenuLabel className="text-xs">Relances</DropdownMenuLabel>
          <DropdownMenuItem onClick={onWhatsApp} data-testid="rec-more-whatsapp">
            <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" /> Relance WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog('schedule')} data-testid="rec-more-schedule">
            <CalendarClock className="mr-2 h-4 w-4 text-blue-600" /> Planifier une relance
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Suivi du dossier</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setDialog('promise')} data-testid="rec-more-promise">
            <HandCoins className="mr-2 h-4 w-4 text-amber-600" /> Promesse de paiement
          </DropdownMenuItem>
          {caseData?.dispute ? (
            <DropdownMenuItem
              onClick={() => call(
                () => axios.delete(`${API}/invoice/${encodeURIComponent(invoiceRef)}/dispute`,
                  { params: { reason: 'Litige résolu' } }),
                'Litige levé',
              )}
              data-testid="rec-more-dispute-clear"
            >
              <AlertOctagon className="mr-2 h-4 w-4 text-gray-500" /> Lever le litige
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => setDialog('dispute')} data-testid="rec-more-dispute">
              <AlertOctagon className="mr-2 h-4 w-4 text-orange-600" /> Marquer en litige
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs">Raccourcis</DropdownMenuLabel>
          <DropdownMenuItem onClick={openLinked} data-testid="rec-more-open-linked">
            <ExternalLink className="mr-2 h-4 w-4 text-gray-500" />
            {detail?.invoiceType === 'operation' ? "Voir l'opération" : 'Voir le contrat'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={copyRecap} data-testid="rec-more-copy">
            <ClipboardCopy className="mr-2 h-4 w-4 text-gray-500" /> Copier le récapitulatif
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setDialog('writeOff')} className="text-red-600"
            disabled={!!caseData?.writeOff} data-testid="rec-more-writeoff">
            <ShieldOff className="mr-2 h-4 w-4" /> Passer en perte (irrécouvrable)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Promesse de paiement */}
      <Dialog open={dialog === 'promise'} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md" data-testid="rec-promise-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Enregistrer une promesse de paiement</DialogTitle>
            <DialogDescription className="text-xs">
              Une tâche de contrôle sera créée automatiquement à la date promise.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date promise</Label>
                <Input type="date" value={promiseDate} onChange={(e) => setPromiseDate(e.target.value)}
                  className="h-9 text-sm" data-testid="rec-promise-date" />
              </div>
              <div>
                <Label className="text-xs">Montant promis (FCFA)</Label>
                <Input type="number" value={promiseAmount} onChange={(e) => setPromiseAmount(e.target.value)}
                  className="h-9 text-sm" data-testid="rec-promise-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Interlocuteur / contexte</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="text-xs"
                placeholder="Ex. M. Koffi (DAF) confirme un virement partiel sous 7 jours"
                data-testid="rec-promise-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={close}>Annuler</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
              disabled={busy || !(Number(promiseAmount) > 0)}
              onClick={() => call(
                () => axios.post(`${API}/invoice/${encodeURIComponent(invoiceRef)}/payment-promise`,
                  { promiseDate, amount: Number(promiseAmount), note: note.trim() || null }),
                'Promesse enregistrée',
              )}
              data-testid="rec-promise-save">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Litige */}
      <Dialog open={dialog === 'dispute'} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md" data-testid="rec-dispute-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Marquer la facture en litige</DialogTitle>
            <DialogDescription className="text-xs">
              La facture reste due mais sort des relances automatiques tant que le litige est ouvert.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Motif de la contestation</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} className="text-xs"
              placeholder="Ex. le client conteste 2 jours d'immobilisation facturés" data-testid="rec-dispute-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={close}>Annuler</Button>
            <Button size="sm" className="bg-orange-600 hover:bg-orange-700"
              disabled={busy || reason.trim().length < 3}
              onClick={() => call(
                () => axios.post(`${API}/invoice/${encodeURIComponent(invoiceRef)}/dispute`,
                  { reason: reason.trim() }),
                'Facture marquée en litige',
              )}
              data-testid="rec-dispute-save">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Passage en perte */}
      <Dialog open={dialog === 'writeOff'} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md" data-testid="rec-writeoff-dialog">
          <DialogHeader>
            <DialogTitle className="text-base text-red-700">Passer la créance en perte</DialogTitle>
            <DialogDescription className="text-xs">
              Action tracée en journal d'audit et réservée à l'administration / la finance.
              La facture sortira du pilotage du recouvrement.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Motif (obligatoire)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} className="text-xs"
              placeholder="Ex. société radiée du RCCM, recouvrement judiciaire infructueux"
              data-testid="rec-writeoff-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={close}>Annuler</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700"
              disabled={busy || reason.trim().length < 3}
              onClick={() => call(
                () => axios.post(`${API}/invoice/${encodeURIComponent(invoiceRef)}/write-off`,
                  { reason: reason.trim() }),
                'Créance passée en perte',
              )}
              data-testid="rec-writeoff-save">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Confirmer la perte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relance planifiée */}
      <Dialog open={dialog === 'schedule'} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-md" data-testid="rec-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Planifier une relance automatique</DialogTitle>
            <DialogDescription className="text-xs">
              Elle partira automatiquement le matin de la date choisie, sauf si la facture est soldée entre-temps.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {[7, 15, 30].map((d) => (
                <Button key={d} type="button" variant="outline" size="sm" className="h-7 text-[11px]"
                  onClick={() => setScheduleDate(inDays(d))} data-testid={`rec-schedule-preset-${d}`}>
                  J+{d}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date d'envoi</Label>
                <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                  className="h-9 text-sm" data-testid="rec-schedule-date" />
              </div>
              <div>
                <Label className="text-xs">Canal</Label>
                <Select value={scheduleChannel} onValueChange={setScheduleChannel}>
                  <SelectTrigger className="h-9 text-sm" data-testid="rec-schedule-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={close}>Annuler</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={busy}
              onClick={() => call(
                () => axios.post(`${API}/invoice/${encodeURIComponent(invoiceRef)}/scheduled-reminders`,
                  { scheduledFor: scheduleDate, channel: scheduleChannel }),
                'Relance planifiée',
              )}
              data-testid="rec-schedule-save">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Planifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DunningMoreActions;
